import { useEffect, useMemo, useState } from "react";
import {
  appel,
  dureeLongue,
  heure,
  jourLong,
  minutes,
  mmss,
  pluriel,
  type QuartierVille,
  type ReglagesVille,
  type ReglesVille,
  type Creneau,
  type SalleVille,
  type Tableau,
  type TribuVille,
} from "./api.js";
import { Apercu, agir, Croix, Dialogue, Frise, Ico, Info, Interrupteur, Kpi, Pas, Point, Reglage, Retour, useEnregistrement, useLecture, useMaintenant } from "./commun.js";
import { useRegie } from "./Regie.js";
import { Fil, LegendeFil, type LigneFil } from "./fil.js";
import { ListeSignalements, DetailSignalement } from "./signalements.js";

/**
 * L'espace Ville (direction A) : ce soir, le fil, les signalements, les
 * réglages, le système. La ville tourne seule ; on la surveille, on
 * intervient quand quelque chose coince.
 */

export function PageVille(): JSX.Element {
  const { route } = useRegie();
  const tableau = useLecture(() => appel<Tableau>("/api/regie/tableau"), 5_000);
  const [tiroir, setTiroir] = useState<string | null>(null);
  const [annonce, setAnnonce] = useState(false);

  const t = tableau.donnees;
  const lignes = useMemo(() => (t ? lignesDe(t.quartiers) : []), [t]);
  const ouverte = tiroir ? (lignes.find((l) => l.salle.id === tiroir) ?? null) : null;

  let page: JSX.Element;
  switch (route.page) {
    case "fil":
      page = <FilVille t={t} lignes={lignes} onTribu={setTiroir} />;
      break;
    case "signalements":
      page = route.id ? <DetailSignalement espace="ville" id={route.id} /> : <ListeSignalements espace="ville" />;
      break;
    case "reglages":
    case "adresses":
    case "textes":
      page = <Reglages onglet={route.page} />;
      break;
    case "systeme":
      page = <Systeme t={t} />;
      break;
    default:
      page = <CeSoir t={t} erreur={tableau.erreur} lignes={lignes} onTribu={setTiroir} onAnnonce={() => setAnnonce(true)} relire={tableau.relire} />;
  }

  return (
    <div className="main">
      {page}
      {ouverte ? <TiroirTribu ligne={ouverte} onFermer={() => setTiroir(null)} relire={tableau.relire} /> : null}
      {annonce && t ? <DialogueAnnonce t={t} onFermer={() => setAnnonce(false)} relire={tableau.relire} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Les tribus, à plat                                                  */
/* ------------------------------------------------------------------ */

export interface Ligne {
  salle: SalleVille;
  tribu: TribuVille;
  quartier: string;
  quartierId: string;
}

function lignesDe(quartiers: QuartierVille[]): Ligne[] {
  const out: Ligne[] = [];
  for (const q of quartiers) {
    for (const s of q.salles) {
      if (s.etat === "closed") continue;
      const tribu = s.tribus[0];
      if (!tribu) continue;
      out.push({ salle: s, tribu, quartier: q.nom, quartierId: q.id });
    }
  }
  return out;
}

const etapeDe = (l: Ligne): string => (l.salle.etat === "planned" ? "inscriptions" : l.tribu.etape);

/** Le chat va s'ouvrir sans le minimum : sans rien faire, la tribu sera annulée. */
function menacee(l: Ligne): boolean {
  const t = l.tribu;
  return etapeDe(l) === "inscriptions" && t.chatOuvreDansS !== null && t.chatOuvreDansS <= 600 && t.membres > 0 && t.membres < t.seuil;
}

function humains(l: Ligne): string {
  const t = l.tribu;
  const e = etapeDe(l);
  if (e === "inscriptions") return t.membres < t.seuil ? `${t.membres} / min. ${t.seuil}` : `${t.membres}`;
  if (e === "rendez-vous" || e === "réunie") return `${t.membres} · ${t.arrives} ${pluriel(t.arrives, "arrivé", "arrivés")}`;
  return `${t.membres}`;
}

function ensuite(l: Ligne, signalements: number): JSX.Element | string {
  const t = l.tribu;
  if (signalements > 0) return <span className="urg">{signalements} {pluriel(signalements, "signalement", "signalements")}</span>;
  const e = etapeDe(l);
  if (e === "inscriptions" && t.chatOuvreDansS !== null) {
    const txt = `chat dans ${minutes(t.chatOuvreDansS)}`;
    return menacee(l) ? <span className="urg">{txt}</span> : txt;
  }
  if (e === "chat") return t.chatSansFin ? "chat sans fin" : t.chatFinitDansS !== null ? `fin du chat dans ${minutes(t.chatFinitDansS)}` : "—";
  return "—";
}

const lieuDe = (l: Ligne): string => `${l.salle.lieu ?? "—"} · ${heure(l.tribu.rdvAt)}`;

/* ------------------------------------------------------------------ */
/* Ce soir (A1)                                                        */
/* ------------------------------------------------------------------ */

const FILTRES = [
  { cle: "toutes", libelle: "Toutes" },
  { cle: "inscriptions", libelle: "Inscriptions" },
  { cle: "chat", libelle: "Chat" },
  { cle: "rendez-vous", libelle: "Rendez-vous" },
  { cle: "réunie", libelle: "Réunies" },
];

function CeSoir(props: {
  t: Tableau | null;
  erreur: string | null;
  lignes: Ligne[];
  onTribu: (id: string) => void;
  onAnnonce: () => void;
  relire: () => Promise<void>;
}): JSX.Element {
  const { aller } = useRegie();
  const [quartier, setQuartier] = useState("");
  const [filtre, setFiltre] = useState("toutes");
  const [ecartees, setEcartees] = useState<string[]>([]);
  const [videsOuvertes, setVidesOuvertes] = useState(false);
  const t = props.t;
  const maintenant = t?.a ?? Date.now();

  const sigParSession = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of t?.signalementsVille ?? []) m.set(s.sessionId, (m.get(s.sessionId) ?? 0) + 1);
    return m;
  }, [t]);

  const ouvertes = props.lignes.filter((l) => l.salle.etat !== "planned");
  const enChat = props.lignes.filter((l) => l.tribu.etape === "chat").length;
  const reuniesCeSoir = props.lignes.filter((l) => l.tribu.reunis).length + (t?.closes ?? []).filter((c) => c.reunis >= 2).length;
  const aTraiter = (t?.signalementsVille ?? []).filter((s) => s.statut === "nouveau");

  const alertes = props.lignes.filter((l) => menacee(l) && !ecartees.includes(l.salle.id));

  const visibles = props.lignes
    .filter((l) => !quartier || l.quartierId === quartier)
    .filter((l) => filtre === "toutes" || etapeDe(l) === filtre)
    .sort((a, b) => Number(menacee(b)) - Number(menacee(a)) || a.tribu.rdvAt - b.tribu.rdvAt);
  const habitees = visibles.filter((l) => l.tribu.membres > 0 || (sigParSession.get(l.salle.id) ?? 0) > 0);
  const vides = visibles.filter((l) => !habitees.includes(l));

  const ligne = (l: Ligne): JSX.Element => (
    <tr key={l.salle.id} className="cliquable" onClick={() => props.onTribu(l.salle.id)}>
      <td>
        <button type="button" className="nom" onClick={() => props.onTribu(l.salle.id)}>
          <Point couleur={l.tribu.couleur} />
          <span>
            <b>{l.tribu.nomTribu}</b>
            <small>{l.tribu.titre}</small>
          </span>
        </button>
      </td>
      <td>{l.quartier}</td>
      <td>{lieuDe(l)}</td>
      <td>
        <Frise etape={etapeDe(l)} />
      </td>
      <td>{humains(l)}</td>
      <td>{ensuite(l, sigParSession.get(l.salle.id) ?? 0)}</td>
    </tr>
  );

  return (
    <>
      <div className="hd">
        <div>
          <h1>Ce soir</h1>
          <div className="sub">
            {jourLong(maintenant)} · mis à jour à {heure(maintenant)}
          </div>
        </div>
        <span className="sp" />
        <span className="seg">
          <button type="button" className="on" aria-pressed="true">
            Tableau
          </button>
          <button type="button" onClick={() => aller({ espace: "ville", page: "fil" })}>
            Fil
          </button>
        </span>
        <button type="button" className="btn" onClick={props.onAnnonce} disabled={!t}>
          <Ico nom="voix" />
          Dire quelque chose à la ville
        </button>
      </div>
      {props.erreur ? <p className="erreur">{props.erreur}</p> : null}
      {t?.annonce ? (
        <div className="alerte">
          <Ico nom="voix" />
          <div className="tx">
            <b>« {t.annonce.texte} »</b>
            <span className="sub">
              Affichée {t.annonce.secteurId ? `dans ${t.quartiers.find((q) => q.id === t.annonce!.secteurId)?.nom ?? "un quartier"}` : "dans toute la ville"} jusqu'à {heure(t.annonce.jusqua)}.
            </span>
          </div>
          <button type="button" className="btn s q" onClick={() => void agir(() => appel("/api/regie/annonce", { method: "DELETE" }), "Annonce retirée.").then(props.relire)}>
            Retirer
          </button>
        </div>
      ) : null}
      <div className="kpis">
        <Kpi valeur={t?.totaux.humains ?? "—"} libelle="humains dans une tribu" />
        <Kpi valeur={t ? ouvertes.length : "—"} libelle="tribus ouvertes" />
        <Kpi valeur={t ? enChat : "—"} libelle="en chat" />
        <Kpi valeur={t ? reuniesCeSoir : "—"} libelle="réunies ce soir" />
        <Kpi valeur={t ? aTraiter.length : "—"} libelle={pluriel(aTraiter.length, "signalement à traiter", "signalements à traiter")} alerte={aTraiter.length > 0} />
      </div>

      {alertes.length + aTraiter.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h2 className="h2">
            À surveiller <span className="pill g">{alertes.length + aTraiter.length}</span>
          </h2>
          {alertes.map((l) => (
            <div className="alerte" key={l.salle.id}>
              <Point couleur={l.tribu.couleur} />
              <div className="tx">
                <b>
                  {l.tribu.nomTribu} · {l.quartier}
                </b>
                <span className="sub">
                  Chat dans {minutes(l.tribu.chatOuvreDansS ?? 0)} · {l.tribu.membres} sur {l.tribu.seuil} minimum
                </span>
              </div>
              <button
                type="button"
                className="btn s p"
                disabled={!l.tribu.forcable}
                title={l.tribu.forcable ? undefined : "Il faut au moins deux inscrits."}
                onClick={() =>
                  void agir(() => appel(`/api/regie/sessions/${l.salle.id}/tribus/${l.tribu.themeId}/demarrer`, { method: "POST" }), "Chat ouvert.").then(props.relire)
                }
              >
                Ouvrir le chat maintenant
              </button>
              <button type="button" className="btn s q" onClick={() => setEcartees((e) => [...e, l.salle.id])}>
                Laisser faire
              </button>
            </div>
          ))}
          {aTraiter.map((s) => (
            <div className="alerte" key={s.id}>
              <Ico nom="signal" />
              <div className="tx">
                <b>
                  Signalement · {s.tribu.nom} · {s.tribu.lieu}
                </b>
                <span className="sub">
                  Reçu à {heure(s.recuA)}, {s.phase === "chat" ? "pendant le chat" : s.phase}.
                </span>
              </div>
              <button type="button" className="btn s" onClick={() => aller({ espace: "ville", page: "signalements", id: s.id })}>
                Traiter
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h2 className="h2">Toutes les tribus</h2>
          <span className="sp" />
          <div className="chips">
            <select className="chip" aria-label="Quartier" value={quartier} onChange={(e) => setQuartier(e.target.value)}>
              <option value="">Tous les quartiers</option>
              {(t?.quartiers ?? [])
                .slice()
                .sort((a, b) => a.nom.localeCompare(b.nom, "fr", { numeric: true }))
                .map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.nom}
                  </option>
                ))}
            </select>
            {FILTRES.map((f) => (
              <button type="button" key={f.cle} className={filtre === f.cle ? "chip on" : "chip"} aria-pressed={filtre === f.cle} onClick={() => setFiltre(f.cle)}>
                {f.libelle}
              </button>
            ))}
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Tribu</th>
              <th>Quartier</th>
              <th>Rendez-vous</th>
              <th>Phase</th>
              <th>Humains</th>
              <th>Ensuite</th>
            </tr>
          </thead>
          <tbody>
            {habitees.map(ligne)}
            {videsOuvertes ? vides.map(ligne) : null}
          </tbody>
        </table>
        {vides.length > 0 ? (
          <button type="button" className="lien" style={{ alignSelf: "flex-start", color: "#B5B5B5" }} onClick={() => setVidesOuvertes((v) => !v)}>
            {videsOuvertes ? "Replier les tribus sans inscrit" : `${vides.length} ${pluriel(vides.length, "tribu sans inscrit", "tribus sans inscrit")} ▸`}
          </button>
        ) : null}
        {t && visibles.length === 0 ? <div className="vide">Aucune tribu ici pour l'instant.</div> : null}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Le détail d'une tribu (A2) et sa clôture (A8)                        */
/* ------------------------------------------------------------------ */

function TiroirTribu(props: { ligne: Ligne; onFermer: () => void; relire: () => Promise<void> }): JSX.Element {
  const [cloture, setCloture] = useState(false);
  const l = props.ligne;
  const t = l.tribu;
  const e = etapeDe(l);
  const rang = e === "inscriptions" ? 1 : e === "chat" ? 2 : e === "rendez-vous" ? 3 : e === "réunie" ? 4 : 0;
  const tribuId = t.id;
  const [reunir, setReunir] = useState(false);
  const base = `/api/regie/sessions/${l.salle.id}`;
  const avancer = (etape: "rendezvous" | "reunir", dit: string): Promise<void> =>
    agir(() => appel(`${base}/tribus/${tribuId}/${etape}`, { method: "POST" }), dit).then(props.relire);
  const prolonger = (m: number): void =>
    void agir(() => appel(`/api/regie/sessions/${l.salle.id}/tribus/${tribuId}/chat`, { method: "POST", corps: { minutes: m } }), m > 0 ? `Chat rallongé de ${m} min.` : `Chat écourté de ${-m} min.`).then(
      props.relire,
    );

  const etape = (n: number, titre: string, detail: string, actions?: JSX.Element): JSX.Element => (
    <>
      <div className="rail">
        <i className={n < rang ? "fait" : n === rang ? "courant" : ""} />
        {n < 4 ? <u className={n < rang ? "fait" : ""} /> : null}
      </div>
      <div className="corps" style={n === 4 ? { paddingBottom: 0 } : undefined}>
        <b className={n > rang ? "avenir" : ""}>{titre}</b>
        <div className="sub">{detail}</div>
        {n === rang && actions ? <div className="actions">{actions}</div> : null}
      </div>
    </>
  );

  return (
    <>
      <div className="voile" onClick={props.onFermer} />
      <aside className="tiroir" aria-label={`Tribu ${t.nomTribu}`}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          <span style={{ marginTop: 8 }}>
            <Point couleur={t.couleur} taille={22} />
          </span>
          <div style={{ flex: "1 1 auto" }}>
            <div style={{ font: "700 28px/1.15 'Atkinson Hyperlegible', sans-serif" }}>{t.nomTribu}</div>
            <div className="sub">{t.titre}</div>
          </div>
          <button type="button" className="rond" aria-label="Fermer" onClick={props.onFermer}>
            <Croix />
          </button>
        </div>
        <div className="sub" style={{ color: "#E5E5E5" }}>
          {l.quartier} · {l.salle.lieu ?? "—"} · rendez-vous {heure(t.rdvAt)}
        </div>
        <div className="kpis" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
          <Kpi valeur={t.membres} libelle={pluriel(t.membres, "membre", "membres")} />
          <Kpi valeur={l.salle.connectes} libelle="en ligne" />
          <Kpi valeur={t.arrives} libelle={pluriel(t.arrives, "arrivé", "arrivés")} />
          <Kpi valeur={t.signalements} libelle={pluriel(t.signalements, "signalement", "signalements")} />
        </div>
        <div className="etapes">
          {etape(
            1,
            "Inscriptions",
            `${t.membres} ${pluriel(t.membres, "inscrit", "inscrits")} · minimum ${t.seuil}, maximum ${t.capacite}`,
            t.forcable ? (
              <button
                type="button"
                className="btn s p"
                onClick={() => void agir(() => appel(`/api/regie/sessions/${l.salle.id}/tribus/${t.themeId}/demarrer`, { method: "POST" }), "Chat ouvert.").then(props.relire)}
              >
                Ouvrir le chat maintenant
              </button>
            ) : undefined,
          )}
          {etape(
            2,
            rang === 2 ? "Chat · en cours" : "Chat",
            rang === 2
              ? t.chatSansFin
                ? "Sans heure de fin."
                : `Se termine dans ${mmss(t.chatFinitDansS ?? 0)}, puis lecture seule.`
              : rang < 2 && t.chatOuvreDansS !== null
                ? `S'ouvre dans ${minutes(t.chatOuvreDansS)}.`
                : "Fermé.",
            tribuId ? (
              <>
                <button type="button" className="btn s p" onClick={() => void avancer("rendezvous", "Rendez-vous maintenant.")}>
                  Passer au rendez-vous
                </button>
                {t.chatSansFin ? null : (
                  <>
                    <button type="button" className="btn s q" onClick={() => prolonger(5)}>
                      +5 min
                    </button>
                    <button type="button" className="btn s q" onClick={() => prolonger(-5)}>
                      −5 min
                    </button>
                  </>
                )}
              </>
            ) : undefined,
          )}
          {etape(
            3,
            `Rendez-vous · ${heure(t.rdvAt)}`,
            `${l.salle.lieu ?? ""}${rang === 3 ? ` · ${t.arrives} ${pluriel(t.arrives, "arrivé", "arrivés")}` : ""}`,
            tribuId ? (
              <button type="button" className="btn s p" disabled={t.membres < 2} onClick={() => setReunir(true)}>
                Réunir la tribu
              </button>
            ) : undefined,
          )}
          {etape(4, "Réunie", t.reunis && t.reunieA ? `À ${heure(t.reunieA)}.` : "Dès 2 arrivés.")}
        </div>
        <a className="lien" href={`/#/collectif?session=${encodeURIComponent(l.salle.id)}`} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          Ouvrir l'écran collectif <Ico nom="externe" />
        </a>
        <div className="danger">
          <span className="etiq">Fin anticipée</span>
          <button type="button" className="btn x" style={{ alignSelf: "flex-start" }} onClick={() => setCloture(true)}>
            Clôturer et effacer…
          </button>
        </div>
      </aside>
      {reunir ? (
        <Dialogue titre={`Réunir ${t.nomTribu} ?`} etroit onFermer={() => setReunir(false)}>
          <p style={{ margin: 0 }}>Chaque membre en route est compté arrivé, les points communs et les points partent.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="btn q" onClick={() => setReunir(false)}>
              Annuler
            </button>
            <button
              type="button"
              className="btn p"
              onClick={() => {
                setReunir(false);
                void avancer("reunir", `${t.nomTribu} réunie.`);
              }}
            >
              Réunir
            </button>
          </div>
        </Dialogue>
      ) : null}
      {cloture ? (
        <Dialogue titre={`Clôturer ${t.nomTribu} et tout effacer ?`} etroit onFermer={() => setCloture(false)}>
          <p style={{ margin: 0 }}>
            {t.membres > 0 ? (
              <>
                Les <b>{t.membres} membres</b> voient « Tribu fermée par la régie » et retournent à la liste.{" "}
              </>
            ) : null}
            Le chat, les réponses et les points communs sont effacés tout de suite.
          </p>
          <p className="sub" style={{ margin: 0 }}>
            Ce n'est pas réversible. Seuls les compteurs de la soirée restent.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="btn q" onClick={() => setCloture(false)}>
              Garder la tribu
            </button>
            <button
              type="button"
              className="btn x"
              onClick={() =>
                void agir(() => appel(`/api/regie/sessions/${l.salle.id}/close`, { method: "POST" }), `${t.nomTribu} clôturée.`).then(async (ok) => {
                  if (ok) {
                    setCloture(false);
                    props.onFermer();
                  }
                  await props.relire();
                })
              }
            >
              Clôturer et effacer
            </button>
          </div>
        </Dialogue>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Dire quelque chose à la ville (A7)                                  */
/* ------------------------------------------------------------------ */

/** Les durées d'une annonce : 30 min, 1 h, toute la soirée, ou de 1 s à 24 h. */
export function ChoixDuree(props: { onChange: (ms: number) => void; maintenant: number }): JSX.Element {
  const [choix, setChoix] = useState<"30" | "60" | "soiree" | "autre">("30");
  const [valeur, setValeur] = useState("45");
  const [unite, setUnite] = useState<"s" | "min" | "h">("min");
  const finDeSoiree = (): number => {
    // Jusqu'à 4 h du matin, heure de Lyon.
    const d = new Date(props.maintenant);
    const h = Number(d.toLocaleString("fr-FR", { hour: "2-digit", hour12: false, timeZone: "Europe/Paris" }));
    const restant = ((24 - h + 4) % 24 || 24) * 3_600_000 - d.getMinutes() * 60_000;
    return Math.min(24 * 3_600_000, Math.max(3_600_000, restant));
  };
  const duree = (): number => {
    if (choix === "30") return 30 * 60_000;
    if (choix === "60") return 3_600_000;
    if (choix === "soiree") return finDeSoiree();
    const n = Number(valeur.replace(",", "."));
    const ms = n * (unite === "s" ? 1000 : unite === "min" ? 60_000 : 3_600_000);
    return Number.isFinite(ms) ? Math.min(24 * 3_600_000, Math.max(1000, Math.round(ms))) : 30 * 60_000;
  };
  const d = duree();
  const onChange = props.onChange;
  useEffect(() => onChange(d), [d, onChange]);
  return (
    <>
      <div className="champ">
        <span className="lb">Reste affichée</span>
        <div className="chips">
          {(
            [
              ["30", "30 min"],
              ["60", "1 h"],
              ["soiree", "Toute la soirée"],
              ["autre", "Autre…"],
            ] as const
          ).map(([c, l]) => (
            <button type="button" key={c} className={choix === c ? "chip on" : "chip"} aria-pressed={choix === c} onClick={() => setChoix(c)}>
              {l}
            </button>
          ))}
        </div>
      </div>
      {choix === "autre" ? (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
          <div className="champ" style={{ width: 110 }}>
            <label htmlFor="rg-duree">Durée</label>
            <input id="rg-duree" className="in" type="number" min={1} value={valeur} onChange={(e) => setValeur(e.target.value)} style={{ textAlign: "center" }} />
          </div>
          <div className="champ" style={{ width: 170 }}>
            <label htmlFor="rg-unite">Unité</label>
            <select id="rg-unite" className="in" value={unite} onChange={(e) => setUnite(e.target.value as "s" | "min" | "h")}>
              <option value="s">secondes</option>
              <option value="min">minutes</option>
              <option value="h">heures</option>
            </select>
          </div>
          <span className="sub" style={{ paddingBottom: 12 }}>
            Effacée à {heure(props.maintenant + d)}.
          </span>
        </div>
      ) : (
        <span className="sub">Effacée à {heure(props.maintenant + d)}.</span>
      )}
    </>
  );
}

function DialogueAnnonce(props: { t: Tableau; onFermer: () => void; relire: () => Promise<void> }): JSX.Element {
  const [texte, setTexte] = useState("");
  const [cible, setCible] = useState<string>("");
  const [dureeMs, setDureeMs] = useState(30 * 60_000);
  const maintenant = useMaintenant(10_000);
  const quartiers = props.t.quartiers.slice().sort((a, b) => a.nom.localeCompare(b.nom, "fr", { numeric: true }));
  const ecrans = props.t.sante.connexions;
  return (
    <Dialogue titre="Dire quelque chose à la ville" onFermer={props.onFermer}>
      <div className="champ">
        <label htmlFor="rg-annonce">Hoa dira cette phrase sur tous les écrans ouverts</label>
        <textarea id="rg-annonce" className="in" rows={2} maxLength={160} style={{ height: 84 }} value={texte} onChange={(e) => setTexte(e.target.value)} autoFocus />
        <span className="sub" style={{ alignSelf: "flex-end" }}>
          {texte.length} / 160
        </span>
      </div>
      <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
        <div className="champ">
          <span className="lb">À qui</span>
          <div className="chips">
            <button type="button" className={!cible ? "chip on" : "chip"} aria-pressed={!cible} onClick={() => setCible("")}>
              Toute la ville
            </button>
            <select className={cible ? "chip on" : "chip"} aria-label="Un quartier" value={cible} onChange={(e) => setCible(e.target.value)}>
              <option value="">Un quartier</option>
              {quartiers.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.nom}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <ChoixDuree maintenant={maintenant} onChange={setDureeMs} />
      <div className="champ">
        <span className="lb">Aperçu sur un téléphone</span>
        <Apercu annonceur={null}>{texte.trim() || "…"}</Apercu>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button type="button" className="btn q" onClick={props.onFermer}>
          Annuler
        </button>
        <button
          type="button"
          className="btn p"
          disabled={texte.trim().length < 2}
          onClick={() =>
            void agir(
              () => appel("/api/regie/annonce", { method: "POST", corps: { texte: texte.trim(), secteurId: cible || undefined, dureeS: Math.round(dureeMs / 1000) } }),
              "Annonce affichée.",
            ).then(async (ok) => {
              if (ok) props.onFermer();
              await props.relire();
            })
          }
        >
          Afficher sur {ecrans} {pluriel(ecrans, "écran", "écrans")}
        </button>
      </div>
    </Dialogue>
  );
}

/* ------------------------------------------------------------------ */
/* Le fil de la soirée (A6)                                            */
/* ------------------------------------------------------------------ */

function FilVille(props: { t: Tableau | null; lignes: Ligne[]; onTribu: (id: string) => void }): JSX.Element {
  const { aller } = useRegie();
  const [videsOuvertes, setVidesOuvertes] = useState(false);
  const maintenant = props.t?.a ?? Date.now();
  const sig = props.t?.signalementsVille ?? [];

  // Même règle que « Ce soir » : des membres, une réunion, ou un signalement.
  const habitees = props.lignes.filter((l) => l.tribu.membres > 0 || l.tribu.reunis || sig.some((z) => z.sessionId === l.salle.id));
  const vides = props.lignes.filter((l) => !habitees.includes(l));
  const montrees = videsOuvertes ? [...habitees, ...vides] : habitees;

  const groupes = new Map<string, LigneFil[]>();
  for (const l of montrees.slice().sort((a, b) => a.quartier.localeCompare(b.quartier, "fr", { numeric: true }))) {
    const t = l.tribu;
    const chatA = t.chatOuvertA ?? (t.chatOuvreDansS !== null ? maintenant + t.chatOuvreDansS * 1000 : t.rdvAt);
    groupes.set(l.quartier, [
      ...(groupes.get(l.quartier) ?? []),
      {
        id: l.salle.id,
        couleur: t.couleur,
        nom: t.nomTribu,
        sous: humains(l),
        ouverte: t.ouverteA ?? maintenant - 3_600_000,
        chatA,
        chatOuvert: t.chatOuvertA != null,
        personne: t.membres === 0,
        finChat: Math.max(chatA, t.rdvAt),
        rdvAt: t.rdvAt,
        reunieA: t.reunis ? (t.reunieA ?? null) : null,
        menacee: menacee(l),
        signalements: sig.filter((z) => z.sessionId === l.salle.id).map((z) => z.recuA),
      },
    ]);
  }

  return (
    <>
      <div className="hd">
        <button type="button" className="rond" aria-label="Revenir au tableau" onClick={() => aller({ espace: "ville", page: "soir" })}>
          <Retour />
        </button>
        <div>
          <h1>
            Le fil de la soirée
            <Info texte="Une ligne par tribu, le trait blanc = maintenant. Toucher une tribu ouvre son détail." />
          </h1>
        </div>
        <span className="sp" />
        <span className="seg">
          <button type="button" onClick={() => aller({ espace: "ville", page: "soir" })}>
            Tableau
          </button>
          <button type="button" className="on" aria-pressed="true">
            Fil
          </button>
        </span>
      </div>
      <Fil groupes={[...groupes].map(([titre, lignes]) => ({ titre, lignes }))} maintenant={maintenant} vide="Aucune tribu habitée pour l'instant." onLigne={props.onTribu} />
      {vides.length > 0 ? (
        <button type="button" className="lien" style={{ alignSelf: "flex-start", color: "#B5B5B5" }} onClick={() => setVidesOuvertes((v) => !v)}>
          {videsOuvertes
            ? "Replier les tribus sans inscrit"
            : `${[...new Set(vides.map((l) => l.quartier))].slice(0, 4).join(", ")}${new Set(vides.map((l) => l.quartier)).size > 4 ? "…" : ""} · ${vides.length} ${pluriel(vides.length, "tribu sans inscrit", "tribus sans inscrit")} ▸`}
        </button>
      ) : null}
      <LegendeFil />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Réglages (A5, A11, A13)                                             */
/* ------------------------------------------------------------------ */

const serie = (a: number, b: number, pas = 1): number[] => {
  const out: number[] = [];
  for (let v = a; v <= b; v += pas) out.push(v);
  return out;
};

function OngletsReglages(props: { actif: string }): JSX.Element {
  const { aller } = useRegie();
  const onglets = [
    ["reglages", "Règles de soirée"],
    ["adresses", "Adresses des quartiers"],
    ["textes", "Textes"],
  ] as const;
  return (
    <nav className="chips" aria-label="Réglages">
      {onglets.map(([p, l]) => (
        <button type="button" key={p} className={props.actif === p ? "chip on" : "chip"} aria-current={props.actif === p ? "page" : undefined} onClick={() => aller({ espace: "ville", page: p })}>
          {l}
        </button>
      ))}
    </nav>
  );
}

function Reglages(props: { onglet: "reglages" | "adresses" | "textes" }): JSX.Element {
  const lecture = useLecture(() => appel<ReglagesVille>("/api/regie/reglages"), null);
  const tableau = useLecture(() => appel<Tableau>("/api/regie/tableau"), null);
  const r = lecture.donnees;
  return (
    <>
      <div className="hd">
        <div>
          <h1>
            Réglages
            <Info texte="Pour toutes les tribus de la ville. Enregistrés à chaque changement, gardés après un redémarrage." />
          </h1>
        </div>
      </div>
      <OngletsReglages actif={props.onglet} />
      {lecture.erreur ? <p className="erreur">{lecture.erreur}</p> : null}
      {r && !r.modifiable ? <p className="erreur">Ce serveur n'a pas de fichier de réglages : rien ne peut être enregistré.</p> : null}
      {!r ? <p className="sub">Chargement…</p> : null}
      {r && props.onglet === "reglages" ? <Regles r={r} veille={tableau.donnees?.veille ?? null} relire={lecture.relire} relireVeille={tableau.relire} /> : null}
      {r && props.onglet === "adresses" ? <Adresses r={r} relire={lecture.relire} /> : null}
      {r && props.onglet === "textes" ? <Textes r={r} relire={lecture.relire} /> : null}
    </>
  );
}

function Regles(props: { r: ReglagesVille; veille: { videsVoulues: number; maxOuvertes: number } | null; relire: () => Promise<void>; relireVeille: () => Promise<void> }): JSX.Element {
  const [regles, setRegles] = useState<ReglesVille>(props.r.regles);
  const [veille, setVeille] = useState(props.veille);
  if (props.veille && !veille) setVeille(props.veille);
  const ok = props.r.modifiable;

  const envoyerRegles = useEnregistrement<Partial<ReglesVille>>(async (v) => {
    await agir(() => appel("/api/regie/reglages", { method: "PUT", corps: v }), "Enregistré.");
    await props.relire();
  });
  const envoyerVeille = useEnregistrement<{ videsVoulues: number; maxOuvertes: number }>(async (v) => {
    await agir(() => appel("/api/regie/veille", { method: "POST", corps: v }), "Enregistré.");
    await props.relireVeille();
  });
  const majRegle = (k: keyof ReglesVille, v: number): void => {
    const suivant = { ...regles, [k]: v };
    if (k === "seuil" && v > suivant.capacite) suivant.capacite = v;
    if (k === "capacite" && v < suivant.seuil) suivant.seuil = v;
    setRegles(suivant);
    envoyerRegles({ seuil: suivant.seuil, capacite: suivant.capacite, [k]: v });
  };
  const majVeille = (k: "videsVoulues" | "maxOuvertes", v: number): void => {
    if (!veille) return;
    const s = { ...veille, [k]: v };
    if (k === "videsVoulues" && v > s.maxOuvertes) s.maxOuvertes = v;
    if (k === "maxOuvertes" && v < s.videsVoulues) s.videsVoulues = v;
    setVeille(s);
    envoyerVeille(s);
  };
  const n = (v: number) => String(v);

  return (
    <div className="grille2">
      <section className="carte carteReg">
        <h2 className="h2">Tribus</h2>
        <Reglage libelle="Minimum pour qu'une tribu parte">
          <Pas libelle="Minimum" valeurs={serie(2, 8)} valeur={regles.seuil} format={n} desactive={!ok} onChange={(v) => majRegle("seuil", v)} />
        </Reglage>
        <Reglage libelle="Maximum par tribu">
          <Pas libelle="Maximum" valeurs={serie(2, 8)} valeur={regles.capacite} format={n} desactive={!ok} onChange={(v) => majRegle("capacite", v)} />
        </Reglage>
      </section>
      <section className="carte carteReg">
        <h2 className="h2">Chat</h2>
        <Reglage libelle="Ouvert avant le rendez-vous">
          <Pas libelle="Ouverture du chat" valeurs={[1, 2, 3, 5, 10, 15, 20, 25, 30, 40, 45, 50, 60]} valeur={regles.dureeChatMin} format={(v) => `${v} min`} desactive={!ok} onChange={(v) => majRegle("dureeChatMin", v)} />
        </Reglage>
        <Reglage libelle="Messages par personne">
          <Pas libelle="Messages" valeurs={[5, 10, 15, 20, 25, 30, 40, 50, 60, 80, 100, 150, 200]} valeur={regles.messagesParPersonne} format={n} desactive={!ok} onChange={(v) => majRegle("messagesParPersonne", v)} />
        </Reglage>
      </section>
      <section className="carte carteReg">
        <h2 className="h2">Sur place</h2>
        <Reglage libelle="Rayon de retrouvailles">
          <Pas libelle="Rayon" valeurs={serie(10, 150, 5)} valeur={regles.rayonM} format={(v) => `${v} m`} desactive={!ok} onChange={(v) => majRegle("rayonM", v)} />
        </Reglage>
        <Reglage libelle="Place rendue après">
          <Pas libelle="Délai d'abandon" valeurs={serie(1, 60)} valeur={regles.delaiAbandonMin} format={(v) => `${v} min`} desactive={!ok} onChange={(v) => majRegle("delaiAbandonMin", v)} />
        </Reglage>
      </section>
      <section className="carte carteReg">
        <h2 className="h2">La veille</h2>
        <Reglage libelle="Tribus vides par quartier">
          <Pas libelle="Tribus vides" valeurs={serie(0, 12)} valeur={veille?.videsVoulues ?? 0} format={n} desactive={!veille} onChange={(v) => majVeille("videsVoulues", v)} />
        </Reglage>
        <Reglage libelle="Plafond de tribus ouvertes">
          <Pas libelle="Plafond" valeurs={serie(1, 20)} valeur={veille?.maxOuvertes ?? 1} format={n} desactive={!veille} onChange={(v) => majVeille("maxOuvertes", v)} />
        </Reglage>
      </section>
      <PendantLaSoiree r={props.r} relire={props.relire} />
      <Sessions r={props.r} relire={props.relire} />
    </div>
  );
}

/** Le compte de rencontres et « J'y suis » sur preuve : venus de la console, réglés ici. */
function PendantLaSoiree(props: { r: ReglagesVille; relire: () => Promise<void> }): JSX.Element {
  const [v, setV] = useState({ couleurArrivee: true, ...(props.r.interrupteurs ?? { montrerRencontres: true, exigerProximite: false }) });
  const changer = (k: "montrerRencontres" | "exigerProximite" | "couleurArrivee", x: boolean): void => {
    setV({ ...v, [k]: x });
    void agir(() => appel("/api/regie/interrupteurs", { method: "PUT", corps: { [k]: x } }), "Enregistré.").then(props.relire);
  };
  return (
    <section className="carte carteReg" style={{ gridColumn: "1 / -1" }}>
      <h2 className="h2">Pendant la soirée</h2>
      <Reglage libelle="Compte de rencontres visible">
        <Interrupteur valeur={v.montrerRencontres} libelle="Compte de rencontres visible" onChange={(x) => changer("montrerRencontres", x)} />
      </Reglage>
      <Reglage libelle="« J'y suis » seulement sur place">
        <Interrupteur valeur={v.exigerProximite} libelle="« J'y suis » seulement sur place" onChange={(x) => changer("exigerProximite", x)} />
      </Reglage>
      <Reglage libelle="Couleur de la tribu en plein écran à l'arrivée" info="Au rendez-vous, chaque téléphone arrivé montre la couleur de sa tribu pour que les autres la repèrent. Vaut aussi pour les tables des événements.">
        <Interrupteur valeur={v.couleurArrivee !== false} libelle="Couleur de la tribu en plein écran à l'arrivée" onChange={(x) => changer("couleurArrivee", x)} />
      </Reglage>
    </section>
  );
}

const JOURS = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

/**
 * Les sessions de la ville (réunion du 28 septembre : « 3 sessions par
 * semaine, ex. le mardi de 19 h à 22 h »). Aucune : Hoa réunit tout le temps.
 */
function Sessions(props: { r: ReglagesVille; relire: () => Promise<void> }): JSX.Element {
  const [liste, setListe] = useState<Creneau[]>(props.r.sessions ?? []);
  const envoyer = useEnregistrement<Creneau[]>(async (v) => {
    await agir(() => appel("/api/regie/sessions", { method: "PUT", corps: { sessions: v } }), "Enregistré.");
    await props.relire();
  }, 900);
  const maj = (v: Creneau[]): void => {
    setListe(v);
    envoyer(v);
  };
  const ok = props.r.modifiable;
  return (
    <section className="carte carteReg" style={{ gridColumn: "1 / -1" }}>
      <h2 className="h2">Sessions</h2>
      {liste.length === 0 ? <div className="reg"><span>Hoa réunit tous les jours, à toute heure.</span><span /></div> : null}
      {liste.map((c, i) => (
        <div className="reg" key={i} style={{ gridTemplateColumns: "minmax(0, 1fr) auto" }}>
          <span style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <select className="in" style={{ width: 170 }} aria-label="Jour" value={c.jour} disabled={!ok} onChange={(e) => maj(liste.map((x, j) => (j === i ? { ...x, jour: Number(e.target.value) } : x)))}>
              {JOURS.map((j, k) => (
                <option key={j} value={k}>
                  {j}
                </option>
              ))}
            </select>
            <span className="sub">de</span>
            <input className="in" type="time" style={{ width: 130 }} aria-label="Début" value={c.debut} disabled={!ok} onChange={(e) => maj(liste.map((x, j) => (j === i ? { ...x, debut: e.target.value } : x)))} />
            <span className="sub">à</span>
            <input className="in" type="time" style={{ width: 130 }} aria-label="Fin" value={c.fin} disabled={!ok} onChange={(e) => maj(liste.map((x, j) => (j === i ? { ...x, fin: e.target.value } : x)))} />
          </span>
          <button type="button" className="btn s q" disabled={!ok} onClick={() => maj(liste.filter((_, j) => j !== i))}>
            Retirer
          </button>
        </div>
      ))}
      <div style={{ padding: "12px 0 4px" }}>
        <button type="button" className="btn s" disabled={!ok || liste.length >= 14} onClick={() => maj([...liste, { jour: 2, debut: "19:00", fin: "22:00" }])}>
          Ajouter une session
        </button>
      </div>
    </section>
  );
}

function Adresses(props: { r: ReglagesVille; relire: () => Promise<void> }): JSX.Element {
  const quartiers = props.r.quartiers.filter((q) => !q.regie).sort((a, b) => a.nom.localeCompare(b.nom, "fr", { numeric: true }));
  const [actif, setActif] = useState(quartiers[0]?.id ?? "");
  const [nom, setNom] = useState("");
  const [repere, setRepere] = useState("");
  const [adresse, setAdresse] = useState("");
  const q = quartiers.find((x) => x.id === actif) ?? quartiers[0];
  if (!q) return <div className="vide">Aucun quartier.</div>;
  const total = q.usine.length + q.ajoutees.length;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "200px minmax(0, 1.2fr) minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
      <nav className="navTables" aria-label="Quartiers">
        {quartiers.map((x) => (
          <button type="button" key={x.id} className={x.id === q.id ? "on" : ""} onClick={() => setActif(x.id)}>
            <span>{x.nom}</span>
            <span>{x.usine.length + x.ajoutees.length}</span>
          </button>
        ))}
      </nav>
      <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <h2 className="h2">
          {q.nom} · {total} {pluriel(total, "adresse", "adresses")}
          <Info texte="Les tribus du quartier tirent leur rendez-vous dans cette liste : un lieu public, éclairé, trouvable de nuit, jamais un domicile." />
        </h2>
        {q.usine.map((u) => (
          <div key={`u-${u}`} style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44, borderBottom: "1px solid #2A2A2A" }}>
            <b>{u}</b>
          </div>
        ))}
        {q.ajoutees.map((l, i) => (
          <div key={`a-${l.name}-${i}`} style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 52, borderBottom: "1px solid #2A2A2A" }}>
            <span style={{ flex: "1 1 auto" }}>
              <b>{l.name}</b>
              {l.landmark ? <span className="sub" style={{ display: "block" }}>{l.landmark}</span> : null}
            </span>
            <span className="pill g">ajoutée</span>
            <button
              type="button"
              className="btn s q"
              disabled={!props.r.modifiable}
              onClick={() => void agir(() => appel(`/api/regie/quartiers/${q.id}/lieux/${i}`, { method: "DELETE" }), "Adresse retirée.").then(props.relire)}
            >
              Retirer
            </button>
          </div>
        ))}
      </section>
      <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 className="h2">Ajouter une adresse</h2>
        <div className="champ">
          <label htmlFor="rg-l-nom">Nom du lieu</label>
          <input id="rg-l-nom" className="in" maxLength={80} value={nom} onChange={(e) => setNom(e.target.value)} />
        </div>
        <div className="champ">
          <label htmlFor="rg-l-rep">Repère</label>
          <input id="rg-l-rep" className="in" maxLength={120} value={repere} onChange={(e) => setRepere(e.target.value)} />
        </div>
        <div className="champ">
          <label htmlFor="rg-l-adr">Adresse postale</label>
          <input id="rg-l-adr" className="in" maxLength={160} value={adresse} onChange={(e) => setAdresse(e.target.value)} />
        </div>
        <button
          type="button"
          className="btn p s"
          style={{ alignSelf: "flex-start" }}
          disabled={!props.r.modifiable || !nom.trim() || !adresse.trim()}
          onClick={() =>
            void agir(() => appel(`/api/regie/quartiers/${q.id}/lieux`, { method: "POST", corps: { name: nom, landmark: repere, address: adresse } }), "Adresse ajoutée.").then(async (ok) => {
              if (ok) {
                setNom("");
                setRepere("");
                setAdresse("");
              }
              await props.relire();
            })
          }
        >
          Ajouter
        </button>
      </section>
    </div>
  );
}

function Textes(props: { r: ReglagesVille; relire: () => Promise<void> }): JSX.Element {
  const [texte, setTexte] = useState(props.r.regles.texteTransparence);
  const envoyer = useEnregistrement<string>(async (v) => {
    await agir(() => appel("/api/regie/reglages", { method: "PUT", corps: { texteTransparence: v } }), "Enregistré.");
    await props.relire();
  }, 900);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
      <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="champ">
          <label htmlFor="rg-transp">Phrase de transparence, montrée dans les règles et au premier lancement</label>
          <textarea
            id="rg-transp"
            className="in"
            rows={3}
            maxLength={400}
            style={{ height: 96 }}
            value={texte}
            disabled={!props.r.modifiable}
            onChange={(e) => {
              setTexte(e.target.value);
              if (e.target.value.trim().length >= 10) envoyer(e.target.value);
            }}
          />
        </div>
        <span className="sub">{texte.length} / 400</span>
      </section>
      <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <span className="etiq">Aperçu</span>
        <Apercu annonceur={null}>{texte}</Apercu>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Système (A14)                                                       */
/* ------------------------------------------------------------------ */

function Systeme(props: { t: Tableau | null }): JSX.Element {
  const t = props.t;
  return (
    <>
      <div className="hd">
        <div>
          <h1>Système</h1>
        </div>
      </div>
      <div className="grille2">
        <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <h2 className="h2">
            Le serveur
            <Info texte={`Protocole v${t?.sante.protocole ?? "—"} · stockage ${t?.sante.stockage === "memory" ? "en mémoire" : (t?.sante.stockage ?? "—")}`} />
          </h2>
          <div className="kpis" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
            <Kpi valeur={t ? dureeLongue(t.sante.depuisMs) : "—"} libelle="en marche" />
            <Kpi valeur={t?.sante.connexions ?? "—"} libelle="connexions" />
            <Kpi valeur={t ? `${t.sante.memoireMo} Mo` : "—"} libelle="mémoire" />
          </div>
        </section>
        <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <h2 className="h2">
            Les portes
            <Info texte="Ouvertes et fermées depuis la console, sur le PC." />
          </h2>
          {[
            ["Entrées", t?.portes.entrees, "ouvertes", "fermées"],
            ["Régie", t?.portes.regie, "ouverte", "fermée"],
          ].map(([l, v, o, f]) => (
            <div key={String(l)} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="vivant">
                <i className={v ? "" : "eteint"} />
              </span>
              <b>
                {String(l)} {v ? String(o) : String(f)}
              </b>
            </div>
          ))}
        </section>
      </div>
      <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <h2 className="h2">Tribus closes ce soir</h2>
        {t && t.closes.length === 0 ? <div className="vide">Aucune tribu close ces dernières heures.</div> : null}
        {t && t.closes.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Tribu</th>
                <th>Fermée à</th>
                <th>Entrés</th>
                <th>Réunis</th>
                <th>Signalements</th>
                <th>Effacement</th>
              </tr>
            </thead>
            <tbody>
              {t.closes.map((c) => (
                <tr key={c.sessionId}>
                  <td>
                    <span className="nom">
                      <Point couleur={c.couleur} />
                      {c.nom}
                      {c.quartier ? ` · ${c.quartier}` : ""}
                    </span>
                  </td>
                  <td>{heure(c.closedAt)}</td>
                  <td>{c.entres}</td>
                  <td>{c.reunis}</td>
                  <td>{c.signalements}</td>
                  <td>{c.purgeOk ? "vérifié" : <span className="urg">à vérifier</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
    </>
  );
}
